/**
 * Creator Studio API (FOR-286). The contract is docs/creator-studio/api.md in
 * Gluska-Forge-API. Studio is on the personal plane: keyed by profile, never by
 * the active license, so none of these calls depend on X-Active-License-Id.
 *
 * apiClient methods already return the response body; the `{data}` envelope is
 * unwrapped here once.
 */
import { apiClient } from "@/lib/api"
import type { CarouselBrand, CarouselSize, CarouselSlide, CarouselTemplate } from "@/lib/studio-carousel/types"

export type StudioPlatform = "linkedin" | "x"
export type StudioFormat =
  | "li_text"
  | "li_text_image"
  | "li_document"
  | "li_poll"
  | "x_single"
  | "x_thread"
  | "x_single_image"
export type ApprovalMode = "auto" | "manual"
export type DraftStatus = "needs_work" | "pending_approval" | "approved" | "posted" | "discarded"
export type CheckStatus = "passed" | "unchecked" | "failed"
export type SlotStatus = "empty" | "pending_approval" | "approved" | "posted"
export type InputStatus = "new" | "transcribing" | "processing" | "mined" | "failed" | "archived"
export type IdeaStatus = "new" | "drafting" | "drafted" | "parked" | "dismissed" | "failed"
export type RunStatus = "queued" | "running" | "succeeded" | "failed"
export type InputKind = "transcript" | "voice_note" | "note" | "interview" | "link"
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"

export interface StudioProfileSummary {
  id: number
  slug: string
  type: "person" | "company"
  display_name: string
  headline: string | null
  status: "active" | "paused"
  approval_mode: ApprovalMode
  company_profile_id: number | null
  client_id: number | null
  site_id: number | null
}

export interface StudioProfileSettings {
  max_ideas_per_input?: number
  max_drafts_per_input?: number
  variants_per_idea?: number
  auto_slot?: boolean
}

export interface StudioProfile extends StudioProfileSummary {
  company: { id: number; slug: string; display_name: string } | null
  people: { id: number; slug: string; display_name: string }[]
  user_id: number | null
  owner_user_id: number | null
  timezone: string
  default_cta: string | null
  default_platforms: StudioPlatform[]
  cadence: Record<Weekday, number>
  slot_times: string[]
  settings: StudioProfileSettings
  voice_guide_version: number | null
  counts: { inputs: number; needs_work: number; pending_approval: number; approved: number; posted: number }
  created_at: string | null
  updated_at: string | null
}

export interface StudioAccess {
  can_access: boolean
  is_admin: boolean
  studio_only: boolean
  profiles: StudioProfileSummary[]
}

export interface BannedTopic {
  key: string
  label: string
  description?: string | null
  patterns?: string[]
}

export interface Pillar {
  name: string
  share?: number | null
  description?: string | null
}

export interface VoiceGuide {
  id: number
  profile_id: number
  version: number
  is_current: boolean
  bio_facts: Record<string, string>
  pillars: Pillar[]
  audience: string | null
  voice_rules: string | null
  do_say: string[]
  dont_say: string[]
  banned_topics: BannedTopic[]
  format_prefs: Record<string, unknown>
  writing_samples: string[]
  derived_style_notes: string | null
  change_note: string | null
  created_by: number | null
  created_at: string | null
}

export type VoiceGuideInput = Partial<
  Pick<VoiceGuide, "bio_facts" | "pillars" | "audience" | "voice_rules" | "do_say" | "dont_say" | "banned_topics" | "writing_samples">
> & { change_note?: string }

export interface StudioRun {
  id: number
  profile_id: number
  input_id: number | null
  idea_id: number | null
  draft_id: number | null
  carousel_id?: number | null
  stage: "mine" | "draft" | "rewrite" | "carousel"
  status: RunStatus
  calls: number
  input_tokens: number
  output_tokens: number
  cost: number
  error: string | null
  started_at: string | null
  finished_at: string | null
  created_at: string | null
}

export interface StudioInputSummary {
  id: number
  profile_id: number
  kind: InputKind
  source: string
  title: string | null
  status: InputStatus
  creator_speaker: string | null
  has_audio: boolean
  excerpt: string
  idea_count: number | null
  draft_count: number | null
  error: string | null
  recorded_at: string | null
  created_by: number | null
  created_at: string | null
}

export interface StudioClaim {
  id: number
  idx: number
  kind: string
  text: string
  verbatim: string | null
  speaker: string | null
  verbatim_found: boolean
  needs_verification: boolean
  blocked_topic: string | null
  used_count: number
}

export interface StudioIdea {
  id: number
  profile_id: number
  input_id: number
  pillar: string | null
  angle: string
  hook_candidates: string[]
  claim_ids: number[]
  platform: StudioPlatform
  format: StudioFormat
  format_reason: string | null
  score: number | null
  status: IdeaStatus
  created_at: string | null
}

export interface StudioInputFull extends StudioInputSummary {
  raw_text: string
  claims: StudioClaim[]
  ideas: StudioIdea[]
  runs: StudioRun[]
}

export interface DraftFlag {
  code: string
  severity: "block" | "warn"
  message: string
  excerpt?: string | null
  topic?: string | null
}

export interface StudioPublication {
  id: number
  draft_id: number
  platform: StudioPlatform
  url: string
  external_id: string | null
  published_at: string | null
  publish_mode: string
  posted_by: number | null
  metrics_updated_at: string | null
  metrics: Record<MetricField, number | null>
}

export const METRIC_FIELDS = [
  "impressions",
  "reactions",
  "comments",
  "reposts",
  "saves",
  "link_clicks",
  "profile_views",
  "follows",
] as const
export type MetricField = (typeof METRIC_FIELDS)[number]

export interface StudioDraft {
  id: number
  profile_id: number
  idea_id: number | null
  input_id: number | null
  platform: StudioPlatform
  format: StudioFormat
  status: DraftStatus
  check_status: CheckStatus
  body: string
  hook: string | null
  parts: { idx: number; text: string }[] | null
  flags: DraftFlag[]
  blocking_flag_count: number
  scores: Record<string, number> | null
  variant_group: string | null
  variant_rank: number | null
  voice_guide_version: number | null
  slot: { id: number; date: string; position: number; local_time: string } | null
  approved_by: number | null
  approved_at: string | null
  posted_at: string | null
  publication: StudioPublication | null
  created_at: string | null
  updated_at: string | null
}

export interface ClaimRef {
  text: string
  claim_idxs: number[]
  claim_ids: number[]
  basis: "claim" | "bio_fact" | "framing"
}

export interface StudioDraftFull extends StudioDraft {
  original_body: string | null
  claim_refs: ClaimRef[] | null
  critique: string | null
  idea: StudioIdea | null
  claims: StudioClaim[]
  revisions: {
    id: number
    author_type: string
    user_id: number | null
    reason: "generated" | "critique_rewrite" | "rewrite_instruction" | "edit" | string
    instruction: string | null
    body: string
    created_at: string | null
  }[]
  siblings: { id: number; status: DraftStatus; variant_rank: number | null; check_status: CheckStatus }[]
}

export type DraftBoard = Record<"needs_work" | "pending_approval" | "approved" | "posted", StudioDraft[]>

export interface CalendarSlot {
  id: number
  date: string
  position: number
  local_time: string
  slot_at: string
  platform: StudioPlatform
  generated_by: "cadence" | "manual" | string
  status: SlotStatus
  draft: {
    id: number
    status: DraftStatus
    format: StudioFormat
    hook: string | null
    check_status: CheckStatus
    blocking_flags: number
  } | null
}

export interface CalendarMonth {
  profile: StudioProfileSummary
  month: string
  platform: StudioPlatform
  timezone: string
  approval_mode: ApprovalMode
  cadence: Record<Weekday, number>
  slot_times: string[]
  counts: Record<SlotStatus, number>
  days: { date: string; weekday: Weekday; slots: CalendarSlot[] }[]
}

export interface InspirationItem {
  id: number
  profile_id: number | null
  platform: StudioPlatform | null
  url: string | null
  author_name: string | null
  author_handle: string | null
  text: string
  format: StudioFormat | null
  why_it_works: string | null
  /** Model-extracted shape: hook type, structure, length, CTA style. Not served by every API version yet. */
  structure_notes?: Record<string, unknown> | null
  tags: string[]
  pillar: string | null
  embedded: boolean
  source: string | null
  added_by: number | null
  created_at: string | null
}

export type InspirationInput = Partial<Omit<InspirationItem, "id" | "embedded" | "added_by" | "created_at">> & { text?: string }

interface Paginated<T> {
  data: T[]
  meta: { current_page: number; last_page: number; total: number }
}

type Params = Record<string, string | number | undefined>

const p = (profile: string | number) => `/studio/profiles/${encodeURIComponent(String(profile))}`

export type CarouselStatus = "generating" | "ready" | "failed"

export interface StudioCarousel {
  id: number
  profile_id: number
  topic: string
  brief: string | null
  slide_count: number
  status: CarouselStatus
  template: CarouselTemplate
  size: CarouselSize
  numbered: boolean
  slides: CarouselSlide[]
  caption: string | null
  error: string | null
  last_run_id: number | null
  created_by: number | null
  created_at: string | null
  updated_at: string | null
}

export interface CarouselBrandPayload {
  resolved: CarouselBrand
  /** overrides saved on the profile; anything missing comes from the linked site or defaults */
  saved: Partial<Record<"name" | "domain" | "handle" | "cta" | "primary" | "accent" | "on_accent" | "ink", string>>
  site_id: number | null
}

export type CarouselBrandInput = Partial<Record<keyof CarouselBrandPayload["saved"], string | null>>

export interface NewCarouselInput {
  topic: string
  brief?: string
  slide_count?: number
  template?: CarouselTemplate
  size?: CarouselSize
  numbered?: boolean
}

export const studioApi = {
  access: () => apiClient.get<{ data: StudioAccess }>("/studio/access").then((r) => r.data),

  profiles: {
    list: (type?: "person" | "company") =>
      apiClient.get<{ data: StudioProfileSummary[] }>("/studio/profiles", { params: { type } }).then((r) => r.data),
    get: (profile: string | number) => apiClient.get<{ data: StudioProfile }>(p(profile)).then((r) => r.data),
    update: (profile: string | number, body: Partial<StudioProfile>) =>
      apiClient.patch<{ data: StudioProfile }>(p(profile), body).then((r) => r.data),
    voiceGuide: (profile: string | number) =>
      apiClient.get<{ data: VoiceGuide | null }>(`${p(profile)}/voice-guide`).then((r) => r.data),
    voiceGuides: (profile: string | number) =>
      apiClient.get<{ data: VoiceGuide[] }>(`${p(profile)}/voice-guides`).then((r) => r.data),
    saveVoiceGuide: (profile: string | number, body: VoiceGuideInput) =>
      apiClient.post<{ data: VoiceGuide }>(`${p(profile)}/voice-guide`, body).then((r) => r.data),
    setCadence: (profile: string | number, body: { cadence: Partial<Record<Weekday, number>>; slot_times?: string[] }) =>
      apiClient.put<{ data: StudioProfile }>(`${p(profile)}/cadence`, body).then((r) => r.data),
  },

  inputs: {
    list: (profile: string | number, params?: Params) =>
      apiClient.get<Paginated<StudioInputSummary>>(`${p(profile)}/inputs`, { params }),
    get: (id: number) => apiClient.get<{ data: StudioInputFull }>(`/studio/inputs/${id}`).then((r) => r.data),
    create: (
      profile: string | number,
      body:
        | { raw_text: string; kind?: InputKind; source?: string; title?: string; creator_speaker?: string; mine?: boolean }
        | FormData,
    ) => apiClient.post<{ data: StudioInputSummary; run: StudioRun | null }>(`${p(profile)}/inputs`, body),
    mine: (id: number) => apiClient.post<{ data: StudioInputSummary; run: StudioRun }>(`/studio/inputs/${id}/mine`),
    archive: (id: number) => apiClient.delete<{ success: boolean }>(`/studio/inputs/${id}`),
  },

  ideas: {
    draft: (id: number) => apiClient.post<{ data: StudioIdea; run: StudioRun }>(`/studio/ideas/${id}/draft`),
    update: (id: number, status: "parked" | "dismissed" | "new") =>
      apiClient.patch<{ data: StudioIdea }>(`/studio/ideas/${id}`, { status }).then((r) => r.data),
  },

  runs: {
    get: (id: number) => apiClient.get<{ data: StudioRun }>(`/studio/runs/${id}`).then((r) => r.data),
  },

  drafts: {
    board: (profile: string | number) =>
      apiClient.get<{ data: DraftBoard }>(`${p(profile)}/drafts`, { params: { board: 1 } }).then((r) => r.data),
    list: (profile: string | number, params?: Params) =>
      apiClient.get<Paginated<StudioDraft>>(`${p(profile)}/drafts`, { params }),
    get: (id: number) => apiClient.get<{ data: StudioDraftFull }>(`/studio/drafts/${id}`).then((r) => r.data),
    update: (id: number, body: { body?: string; parts?: { text: string }[] }) =>
      apiClient.patch<{ data: StudioDraftFull }>(`/studio/drafts/${id}`, body).then((r) => r.data),
    rewrite: (id: number, instruction: string) =>
      apiClient.post<{ data: StudioDraft; run: StudioRun }>(`/studio/drafts/${id}/rewrite`, { instruction }),
    approve: (id: number) => apiClient.post<{ data: StudioDraft }>(`/studio/drafts/${id}/approve`).then((r) => r.data),
    unapprove: (id: number) => apiClient.post<{ data: StudioDraft }>(`/studio/drafts/${id}/unapprove`).then((r) => r.data),
    discard: (id: number) => apiClient.post<{ data: StudioDraft }>(`/studio/drafts/${id}/discard`).then((r) => r.data),
    dismissFlag: (id: number, code: string, excerpt?: string | null) =>
      apiClient
        .post<{ data: StudioDraftFull }>(`/studio/drafts/${id}/dismiss-flag`, { code, excerpt: excerpt ?? undefined })
        .then((r) => r.data),
    schedule: (id: number, date: string, position?: number) =>
      apiClient.post<{ data: StudioDraft; slot: CalendarSlot }>(`/studio/drafts/${id}/schedule`, { date, position }),
    unschedule: (id: number) => apiClient.post<{ data: StudioDraft }>(`/studio/drafts/${id}/unschedule`).then((r) => r.data),
    markPosted: (id: number, body: { url: string; published_at?: string; published_body?: string }) =>
      apiClient.post<{ data: StudioDraft }>(`/studio/drafts/${id}/mark-posted`, body).then((r) => r.data),
  },

  publications: {
    update: (id: number, body: Partial<Record<MetricField, number | null>> & { url?: string }) =>
      apiClient.patch<{ data: StudioPublication }>(`/studio/publications/${id}`, body).then((r) => r.data),
  },

  calendar: {
    month: (profile: string | number, month: string, platform: StudioPlatform) =>
      apiClient.get<{ data: CalendarMonth }>(`${p(profile)}/calendar`, { params: { month, platform } }).then((r) => r.data),
  },

  carousels: {
    list: (profile: string | number, params?: { per_page?: number; page?: number }) =>
      apiClient.get<{ data: StudioCarousel[]; meta: { current_page: number; last_page: number; total: number } }>(`${p(profile)}/carousels`, { params }),
    create: (profile: string | number, body: NewCarouselInput) =>
      apiClient.post<{ data: StudioCarousel; run: StudioRun }>(`${p(profile)}/carousels`, body),
    get: (id: number) => apiClient.get<{ data: StudioCarousel }>(`/studio/carousels/${id}`).then((r) => r.data),
    update: (id: number, body: Partial<Pick<StudioCarousel, "topic" | "brief" | "template" | "size" | "numbered" | "caption" | "slides">>) =>
      apiClient.patch<{ data: StudioCarousel }>(`/studio/carousels/${id}`, body).then((r) => r.data),
    regenerate: (id: number, body: { topic?: string; brief?: string | null; slide_count?: number }) =>
      apiClient.post<{ data: StudioCarousel; run: StudioRun }>(`/studio/carousels/${id}/regenerate`, body),
    remove: (id: number) => apiClient.delete<{ success: boolean }>(`/studio/carousels/${id}`),
  },

  carouselBrand: {
    get: (profile: string | number) => apiClient.get<{ data: CarouselBrandPayload }>(`${p(profile)}/carousel-brand`).then((r) => r.data),
    update: (profile: string | number, body: CarouselBrandInput) =>
      apiClient.put<{ data: CarouselBrandPayload }>(`${p(profile)}/carousel-brand`, body).then((r) => r.data),
    uploadLogo: (profile: string | number, file: File) => {
      const form = new FormData()
      form.append("logo", file)
      return apiClient.post<{ data: CarouselBrandPayload }>(`${p(profile)}/carousel-brand/logo`, form).then((r) => r.data)
    },
    removeLogo: (profile: string | number) =>
      apiClient.delete<{ data: CarouselBrandPayload }>(`${p(profile)}/carousel-brand/logo`).then((r) => r.data),
  },

  inspiration: {
    list: (params?: Params) => apiClient.get<{ data: InspirationItem[] }>("/studio/inspiration", { params }).then((r) => r.data),
    create: (body: InspirationInput) =>
      apiClient.post<{ data: InspirationItem }>("/studio/inspiration", body).then((r) => r.data),
    update: (id: number, body: InspirationInput) =>
      apiClient.patch<{ data: InspirationItem }>(`/studio/inspiration/${id}`, body).then((r) => r.data),
    remove: (id: number) => apiClient.delete<{ success: boolean }>(`/studio/inspiration/${id}`),
  },
}
